import { useState } from "react";
import { MagicWand, Funnel, CaretRight } from "@phosphor-icons/react";

const TERM_GROUPS = [
  { key: "title_variants", label: "Title variants" },
  { key: "role_families", label: "Role families" },
  { key: "related_role_families", label: "Role families" },
  { key: "skill_synonyms", label: "Skill synonyms" },
  { key: "seniority_terms", label: "Seniority" },
  { key: "industries", label: "Industries" },
  { key: "keywords", label: "Keywords" },
];

export function SearchPlanCard({ plan, kind = "jobs" }) {
  if (!plan) return null;
  const seenLabels = new Set();
  const groups = TERM_GROUPS.filter((g) => {
    if (!(plan[g.key] && plan[g.key].length)) return false;
    if (seenLabels.has(g.label)) return false;
    seenLabels.add(g.label);
    return true;
  });
  const intents = plan.search_intents || [];
  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-4" data-testid="search-plan-card">
      <div className="flex items-center gap-2 text-indigo-900">
        <MagicWand weight="fill" className="text-indigo-500" />
        <p className="font-semibold text-sm">AI search plan</p>
      </div>
      <p className="mt-1 text-xs text-indigo-800/80">
        Your locked parameters were expanded into {kind === "jobs" ? "job" : "candidate"} searches. Hard rules are never relaxed — eligibility is decided by the deterministic engine below.
      </p>
      {plan.error && <p className="mt-2 text-xs text-orange-700">Plan generation degraded ({plan.error}). Falling back to full pool.</p>}
      {intents.length > 0 && (
        <ul className="mt-3 space-y-1" data-testid="search-intents">
          {intents.map((s, i) => (
            <li key={i} className="text-xs text-indigo-900 flex gap-2"><span className="text-indigo-400">→</span>{s}</li>
          ))}
        </ul>
      )}
      <div className="mt-3 space-y-2">
        {groups.map((g) => (
          <div key={g.key} className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] uppercase tracking-wide text-indigo-500 font-medium w-full sm:w-auto sm:mr-1">{g.label}</span>
            {plan[g.key].slice(0, 12).map((t, i) => (
              <span key={i} className="text-xs bg-white border border-indigo-200 rounded-full px-2 py-0.5 text-indigo-800">{t}</span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function MatchFunnel({ funnel, kind = "jobs" }) {
  const [openStep, setOpenStep] = useState(null);
  const [openGroup, setOpenGroup] = useState(null);
  if (!funnel) return null;
  const total = kind === "jobs" ? funnel.total_jobs : funnel.pool_size;
  const details = funnel.details || {};
  const notRelevant = details.not_relevant || { count: 0, examples: [] };
  const notEligible = details.not_eligible || [];
  const noun = kind === "jobs" ? "roles" : "candidates";

  const steps = [
    { key: "pool", label: kind === "jobs" ? "Jobs in pool" : "Discoverable pool", value: total },
    { key: "retrieved", label: "Retrieved by AI plan", value: funnel.retrieved,
      drill: notRelevant.count > 0 ? "not_relevant" : null },
    { key: "not_eligible", label: "Excluded — not eligible (hard rules)", value: funnel.not_eligible, tone: "red",
      drill: notEligible.length > 0 ? "not_eligible" : null },
    { key: "possibly", label: "Possibly eligible (needs verification)", value: funnel.possibly_eligible, tone: "amber" },
    { key: "eligible", label: "Eligible", value: funnel.eligible, tone: "green" },
    { key: "shortlisted", label: kind === "jobs" ? "Shown to you" : "Shortlisted", value: funnel.shortlisted, tone: "bold" },
  ];
  const toneCls = { red: "text-red-700", amber: "text-orange-700", green: "text-emerald-700", bold: "text-slate-900 font-semibold" };
  const toggle = (i) => { setOpenStep(openStep === i ? null : i); setOpenGroup(null); };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="match-funnel">
      <div className="flex items-center gap-2 text-slate-700"><Funnel weight="fill" className="text-slate-400" /><p className="font-semibold text-sm">Matching funnel</p></div>
      <div className="mt-3 space-y-1">
        {steps.map((s, i) => {
          const clickable = !!s.drill;
          return (
            <div key={s.key}>
              <button
                type="button"
                disabled={!clickable}
                onClick={() => clickable && toggle(i)}
                data-testid={`funnel-step-${s.key}`}
                className={`w-full flex items-center justify-between text-sm py-1 rounded ${clickable ? "hover:bg-slate-50 cursor-pointer" : "cursor-default"}`}
              >
                <span className={`flex items-center gap-1 text-slate-600 ${s.tone === "bold" ? "font-medium text-slate-800" : ""}`}>
                  {clickable && <CaretRight weight="bold" className={`text-slate-400 transition-transform ${openStep === i ? "rotate-90" : ""}`} size={12} />}
                  {s.label}
                </span>
                <span className={`font-mono-num ${toneCls[s.tone] || "text-slate-700"}`}>{s.value ?? "—"}</span>
              </button>

              {openStep === i && s.drill === "not_relevant" && (
                <div className="ml-4 mb-2 rounded-md bg-slate-50 border border-slate-200 p-2.5" data-testid="drill-not-relevant">
                  <p className="text-xs text-slate-600 mb-1">{notRelevant.count} {noun} filtered out as not relevant to the generated search plan:</p>
                  <div className="flex flex-wrap gap-1">
                    {notRelevant.examples.map((n, k) => <span key={k} className="text-[11px] bg-white border border-slate-200 rounded px-1.5 py-0.5 text-slate-500">{n}</span>)}
                    {notRelevant.count > notRelevant.examples.length && <span className="text-[11px] text-slate-400">+{notRelevant.count - notRelevant.examples.length} more</span>}
                  </div>
                </div>
              )}

              {openStep === i && s.drill === "not_eligible" && (
                <div className="ml-4 mb-2 space-y-1" data-testid="drill-not-eligible">
                  {notEligible.map((g, gi) => (
                    <div key={gi} className="rounded-md bg-red-50/60 border border-red-100">
                      <button type="button" onClick={() => setOpenGroup(openGroup === gi ? null : gi)}
                        data-testid={`reason-group-${gi}`}
                        className="w-full flex items-center justify-between px-2.5 py-1.5 text-xs text-red-800 hover:bg-red-50">
                        <span className="flex items-center gap-1">
                          <CaretRight weight="bold" className={`text-red-400 transition-transform ${openGroup === gi ? "rotate-90" : ""}`} size={11} />
                          {g.group}
                        </span>
                        <span className="font-mono-num">{g.count}</span>
                      </button>
                      {openGroup === gi && (
                        <ul className="px-3 pb-2 space-y-1">
                          {g.examples.map((ex, ei) => (
                            <li key={ei} className="text-[11px] text-red-900/80"><span className="font-medium">{ex.label}:</span> {ex.explanation}</li>
                          ))}
                          {g.count > g.examples.length && <li className="text-[11px] text-red-400">+{g.count - g.examples.length} more</li>}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
